import { ButtonVariant } from '@vodafoneziggo/sandwich/components/button';
import { AISearchApi } from 'api';
import { DislikeStroke } from 'assets/icons/DislikeStroke';
import { LikeStroke } from 'assets/icons/LikeStroke';
import { TextArea } from 'components/Form';
import React, { MouseEvent, useState } from 'react';
import { getGASessionId } from 'utils/analytics';
import { ContentUtil } from 'utils/content';

import { content } from './ai-search.content';
import * as S from './ai-search.styles';

interface AIFeedbackProps {
  searchId: string | null;
}

const contentUtil = new ContentUtil({ content, componentName: 'AIFeedback' });

export const AIFeedback = ({ searchId }: AIFeedbackProps) => {
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);
  const [showExtendsedForm, setShowExtendedForm] = useState(false);
  const [likeActive, setLikeActive] = useState(false);
  const [dislikeActive, setDislikeActive] = useState(false);

  const handleFeedback = (isCorrect: boolean) => (event: MouseEvent) => {
    event.preventDefault();

    if (likeActive || dislikeActive) return;

    setShowExtendedForm(true);

    setLikeActive(isCorrect);
    setDislikeActive(!isCorrect);

    postFeedback(searchId, { isCorrect });
  };

  const handleExtendedFeedback = async (values: Record<string, any>): Promise<void> => {
    if (!values.comment || !values.comment.trim().length) return;

    setShowExtendedForm(false);
    setHasSubmittedFeedback(true);

    await postFeedback(searchId, {
      comment: values.comment,
    });
  };

  const postFeedback = async (
    searchId: string | null,
    data: {
      isCorrect?: boolean;
      comment?: string;
    }
  ) => {
    try {
      if (!searchId) {
        console.error('Search ID is required to post feedback');
        return;
      }

      const sessionId = getGASessionId();
      await AISearchApi.postSearchResultFeedback(searchId, sessionId, {
        is_correct: data.isCorrect,
        comment: data.comment,
      });
    } catch (e) {
      console.error('Error posting feedback:', e);
    }
  };

  if (hasSubmittedFeedback) {
    return (
      <div data-testid="ai-search-feedback-success">
        <S.SubmitButton active={false} disabled>
          {contentUtil.translateToString('feedbackSuccess')}
        </S.SubmitButton>
      </div>
    );
  }

  return (
    <div data-testid="ai-search-feedback">
      <S.SearchFeedbackWrapper>
        <p>{contentUtil.translateToString('feedback')}</p>
        <S.FeedbackButtonIcon
          type="button"
          data-testid="like-feedback"
          disabled={likeActive || dislikeActive}
          variant={S.ButtonVariant.SUCCESS}
          active={likeActive}
          onClick={handleFeedback(true)}>
          <LikeStroke />
        </S.FeedbackButtonIcon>
        <S.FeedbackButtonIcon
          type="button"
          data-testid="dislike-feedback"
          disabled={likeActive || dislikeActive}
          variant={S.ButtonVariant.ERROR}
          active={dislikeActive}
          onClick={handleFeedback(false)}>
          <DislikeStroke />
        </S.FeedbackButtonIcon>
      </S.SearchFeedbackWrapper>
      {showExtendsedForm && (
        <S.ExtendedFeedbackForm
          onSubmit={handleExtendedFeedback}
          skipScrollToRef
          testid="extended-feedback-form"
          hideCancelButton
          buttonText={contentUtil.translateToString('submitFeedback')}
          buttonVariant={ButtonVariant.PRIMARY}
          render={() => (
            <TextArea
              maxLength={500}
              placeholder={contentUtil.translateToString('feedbackPlaceholder')}
              name="comment"
            />
          )}
        />
      )}
    </div>
  );
};
